import styled from 'styled-components';
import {TicketDataTableTotalsRow} from './TicketDataTableTotalsRow';
import {TicketDataTable} from './TicketDataTable';
import {DashboardGroupedTickets} from './DashboardGroupedTickets';
import {LoadMoreButton} from '../../tickets/components/LoadMoreButton';
import type {DashboardColumnKey} from '../constants/dashboardColumns';
import type {DashboardTicket, DashboardTotals} from '../types/dashboardTicket';
import type {DashboardGroupByToken, TicketGroup, TicketGroupStats} from '../utils/groupTickets';

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
`;

const LoadMoreWrap = styled.div`
	display: flex;
	justify-content: center;
	padding-top: ${p => p.theme.sys.spacing[300]};
`;

interface Props {
	tickets: readonly DashboardTicket[];
	/**
	 * The rollups above the table. Passed in rather than derived here so the route
	 * can swap a client-side rollup over the loaded rows for the server-side one over
	 * the whole filtered set without touching this component.
	 */
	totals: DashboardTotals;
	/**
	 * The SLA achievement figure as whole percentage points (0–100), or null when
	 * the analytics gauge has no answer. Kept off `totals` because it is sourced
	 * from a different query than the other four figures.
	 */
	slaAchievementRatePct: number | null;
	groupBy: DashboardGroupByToken;
	/** Server-side per-group sub-totals. Forwarded to DashboardGroupedTickets unchanged. */
	groupStats?: Map<string, TicketGroupStats> | null;
	visibleColumns: ReadonlySet<DashboardColumnKey>;
	hasNextPage: boolean;
	isLoadingNext: boolean;
	onLoadMore: () => void;
}

/**
 * The dashboard's ticket table with its totals row, laid out by the active
 * grouping.
 *
 * The totals row sits above the grouping rather than inside it: it rolls up every
 * ticket in scope, so under a grouping it belongs above all the buckets, and each
 * bucket's own subtotals ride on its header. It renders even with nothing in scope —
 * a "Total 0" is an answer, where an absent row leaves the reader unsure whether the
 * query ran.
 */
export function DashboardTicketSection({
	tickets,
	totals,
	slaAchievementRatePct,
	groupBy,
	groupStats,
	visibleColumns,
	hasNextPage,
	isLoadingNext,
	onLoadMore,
}: Props) {
	return (
		<Section data-testid="dashboard-ticket-section">
			<TicketDataTableTotalsRow totals={totals} slaAchievementRatePct={slaAchievementRatePct} />
			<DashboardGroupedTickets
				tickets={tickets}
				groupBy={groupBy}
				groupStats={groupStats}
				renderGroup={(groupTickets: DashboardTicket[], group: TicketGroup<DashboardTicket>) => (
					<TicketDataTable
						tickets={groupTickets}
						visibleColumns={visibleColumns}
						testId={groupBy === 'none' ? 'ticket-data-table' : `ticket-data-table-${group.domKey}`}
					/>
				)}
			/>
			{hasNextPage && tickets.length > 0 && (
				<LoadMoreWrap>
					<LoadMoreButton onClick={onLoadMore} loading={isLoadingNext} />
				</LoadMoreWrap>
			)}
		</Section>
	);
}
